import { watch } from "vue";
import { useUser } from "@/api/user.store";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import { usePresenceStore, type PresenceSnapshot } from "@editor/store/presence.store";
import eventHub from "@/helpers/utility/Events";

type PersistedPresencePayload = {
  version: 1;
  savedAt: number;
  presence: PresenceSnapshot;
};

const PRESENCE_STORAGE_VERSION = 1;
const PRESENCE_STORAGE_KEY_PREFIX = `editorPresence_v${PRESENCE_STORAGE_VERSION}`;
const MODEL_SAVED_EVENT_LISTENER_ID = "presence-persistence-model-saved";
const PRESENCE_TTL_MS = 1000 * 60 * 60 * 24 * 90;

let isStarted = false;
let currentStorageKey: string | null = null;
let stopContextWatcher: (() => void) | null = null;

function hashToken(input: string) {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index++) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function createUserHash(userId: string | number) {
  return hashToken(String(userId));
}

function createModelHash(modelType: string, modelId: string | number) {
  return hashToken(`${modelType}:${modelId}`);
}

function getStorageKeys() {
  const storageKeys: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key != null) {
      storageKeys.push(key);
    }
  }
  return storageKeys;
}

function removePresenceEntriesForUser(userId: string | number) {
  const userHash = createUserHash(userId);
  const userKeyPrefix = `${PRESENCE_STORAGE_KEY_PREFIX}_u${userHash}_`;
  for (const storageKey of getStorageKeys()) {
    if (storageKey.startsWith(userKeyPrefix)) {
      localStorage.removeItem(storageKey);
    }
  }
}

function pruneExpiredPresenceEntries() {
  const now = Date.now();
  const keyPrefix = `${PRESENCE_STORAGE_KEY_PREFIX}_`;

  for (const storageKey of getStorageKeys()) {
    if (!storageKey.startsWith(keyPrefix)) {
      continue;
    }

    const rawValue = localStorage.getItem(storageKey);
    if (rawValue == null) {
      continue;
    }

    try {
      const payload = JSON.parse(rawValue) as Partial<PersistedPresencePayload> | null;
      const savedAt = payload?.savedAt;
      if (
        payload == null
        || payload.version !== PRESENCE_STORAGE_VERSION
        || typeof savedAt !== "number"
        || savedAt + PRESENCE_TTL_MS < now
      ) {
        localStorage.removeItem(storageKey);
      }
    } catch (_error) {
      localStorage.removeItem(storageKey);
    }
  }
}

function getPresenceStorageKey() {
  const { user } = useUser();
  const { loadedModel } = useLoadedModel();

  const userId = user.value?.id;
  const modelId = loadedModel.id;
  const modelType = loadedModel.type;

  if (userId == null || modelId == null || modelType == null) {
    return null;
  }

  const userHash = createUserHash(userId);
  const modelHash = createModelHash(modelType, modelId);
  return `${PRESENCE_STORAGE_KEY_PREFIX}_u${userHash}_m${modelHash}`;
}

function loadPayload(storageKey: string): PersistedPresencePayload | null {
  const rawValue = localStorage.getItem(storageKey);
  if (rawValue == null) {
    return null;
  }

  try {
    const payload = JSON.parse(rawValue) as Partial<PersistedPresencePayload> | null;
    if (
      payload == null
      || payload.version !== PRESENCE_STORAGE_VERSION
      || typeof payload.savedAt !== "number"
      || payload.savedAt + PRESENCE_TTL_MS < Date.now()
      || payload.presence == null
      || typeof payload.presence !== "object"
    ) {
      localStorage.removeItem(storageKey);
      return null;
    }
    return payload as PersistedPresencePayload;
  } catch (_error) {
    localStorage.removeItem(storageKey);
    return null;
  }
}

function saveNow(storageKey: string | null = currentStorageKey) {
  if (storageKey == null) {
    return;
  }

  const presenceStore = usePresenceStore();
  const payload: PersistedPresencePayload = {
    version: PRESENCE_STORAGE_VERSION,
    savedAt: Date.now(),
    presence: presenceStore.getSnapshot()
  };
  localStorage.setItem(storageKey, JSON.stringify(payload));
}

function loadPresenceForCurrentContext() {
  const presenceStore = usePresenceStore();
  const nextStorageKey = getPresenceStorageKey();

  if (nextStorageKey === currentStorageKey) {
    return;
  }

  currentStorageKey = nextStorageKey;

  if (currentStorageKey == null) {
    presenceStore.resetPresence();
    return;
  }

  const payload = loadPayload(currentStorageKey);
  if (payload == null) {
    presenceStore.resetPresence();
    return;
  }

  presenceStore.importSnapshot(payload.presence);
}

function savePresenceForCurrentContext() {
  const storageKey = getPresenceStorageKey();
  if (storageKey == null) {
    return;
  }

  currentStorageKey = storageKey;
  saveNow(storageKey);
}

export function startPresencePersistence() {
  if (isStarted) {
    return;
  }

  isStarted = true;
  const { loadedModel } = useLoadedModel();
  const { user } = useUser();

  pruneExpiredPresenceEntries();

  eventHub.on("model-saved", savePresenceForCurrentContext, MODEL_SAVED_EVENT_LISTENER_ID);

  stopContextWatcher = watch(
    [() => loadedModel.id, () => loadedModel.type, () => user.value?.id],
    (newValues, oldValues) => {
      const [nextUserId] = newValues;
      const oldUserId = oldValues?.[0];

      if (
        oldUserId != null
        && nextUserId !== oldUserId
      ) {
        removePresenceEntriesForUser(oldUserId);
      }

      loadPresenceForCurrentContext();
    },
    { immediate: true }
  );
}

export function stopPresencePersistence() {
  if (!isStarted) {
    return;
  }

  eventHub.off("model-saved", savePresenceForCurrentContext, MODEL_SAVED_EVENT_LISTENER_ID);

  stopContextWatcher?.();
  stopContextWatcher = null;

  currentStorageKey = null;
  isStarted = false;
}
